import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import format from "date-fns/format";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom hooks
import useDepartmentMembersData from "layouts/tables/data/departmentMemberDataTable";

function DepartmentMembers({ departmentId }) {
  const {
    columns,
    attendanceColumns,
    rows,
    attendanceRows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMember,
    setSelectedMember,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMember,
    showAttendanceView,
    setShowAttendanceView,
    toggleAttendanceView,
    showOnlyUnchecked,
    setShowOnlyUnchecked,
    dateRange,
    setDateRange,
    fetchDepartmentAttendance,
    filteredAttendanceRows,
  } = useDepartmentMembersData(departmentId);

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const handleStartDateChange = (date) => {
    const newDateRange = { ...dateRange, startDate: date };
    setDateRange(newDateRange);
    fetchDepartmentAttendance(date, dateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = { ...dateRange, endDate: date };
    setDateRange(newDateRange);
    fetchDepartmentAttendance(dateRange.startDate, date);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setDateRange({ startDate: today, endDate: today });
    fetchDepartmentAttendance(today, today);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading department members...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      {showAttendanceView ? "Department Attendance" : "Department Members"} -{" "}
                      {formatDisplayDate(dateRange.startDate)}
                      {dateRange.startDate.getTime() !== dateRange.endDate.getTime() &&
                        ` to ${formatDisplayDate(dateRange.endDate)}`}
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      {showAttendanceView && (
                        <>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="From Date"
                              value={dateRange.startDate}
                              onChange={handleStartDateChange}
                              maxDate={dateRange.endDate}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    backgroundColor: "white",
                                    borderRadius: 1,
                                    width: 150,
                                  }}
                                />
                              )}
                            />
                            <DatePicker
                              label="To Date"
                              value={dateRange.endDate}
                              onChange={handleEndDateChange}
                              minDate={dateRange.startDate}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    backgroundColor: "white",
                                    borderRadius: 1,
                                    width: 150,
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                          <MDButton variant="outlined" color="white" onClick={handleTodayClick}>
                            Today
                          </MDButton>
                          <MDButton
                            variant={showOnlyUnchecked ? "contained" : "outlined"}
                            color="white"
                            onClick={() => setShowOnlyUnchecked(!showOnlyUnchecked)}
                          >
                            {showOnlyUnchecked ? "Show All" : "Show Only Unchecked"}
                          </MDButton>
                        </>
                      )}
                      <MDButton
                        variant={showAttendanceView ? "contained" : "outlined"}
                        color="white"
                        onClick={toggleAttendanceView}
                      >
                        {showAttendanceView ? "View Members" : "View Attendance"}
                      </MDButton>
                      {!showAttendanceView && (
                        <>
                          <MDButton variant="gradient" color="dark" onClick={handleAddMember}>
                            <Icon>add</Icon>&nbsp;Add Member
                          </MDButton>
                          <MDButton variant="gradient" color="dark" onClick={handlePrint}>
                            <Icon>print</Icon>
                          </MDButton>
                        </>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: showAttendanceView ? attendanceColumns : columns,
                    rows: showAttendanceView ? filteredAttendanceRows : rows,
                  }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel={showAttendanceView ? "Search attendance..." : "Search members..."}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit Member Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <MDBox p={3}>
          <MDTypography variant="h6" gutterBottom>
            {selectedMember?.id ? "Edit Member" : "Add New Member"}
          </MDTypography>
          <MDInput
            label="Member Name"
            value={selectedMember?.name || ""}
            onChange={(e) =>
              setSelectedMember({
                ...selectedMember,
                name: e.target.value,
              })
            }
            fullWidth
            margin="normal"
          />
          <MDBox display="flex" justifyContent="flex-end" mt={3}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setEditModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton
              style={{ marginLeft: 10 }}
              variant="gradient"
              color="info"
              onClick={handleEditSubmit}
            >
              {selectedMember?.id ? "Save Changes" : "Add Member"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      {/* Delete Member Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <MDBox p={3}>
          <MDTypography variant="h6" gutterBottom>
            Confirm Delete
          </MDTypography>
          <MDTypography>
            Are you sure you want to delete &quot;{selectedMember?.name}&quot;?
          </MDTypography>
          <MDBox display="flex" justifyContent="flex-end" mt={3}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setDeleteModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton
              style={{ marginLeft: 10 }}
              variant="gradient"
              color="error"
              onClick={handleDeleteConfirm}
            >
              Confirm Delete
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

DepartmentMembers.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default DepartmentMembers;
